import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fmt, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Loader2, Package, CalendarClock, LogOut } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  new: "text-zinc-400", awaiting_deposit: "text-yellow-400", deposit_paid: "text-blue-400",
  approved: "text-brand", balance_due: "text-orange-400", completed: "text-green-400",
  declined: "text-red-400", pending: "text-yellow-400", paid: "text-green-400",
};

export default function AccountPage() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState({ name: "", phone: "", ig: "" });

  useEffect(() => {
    if (!user) return;
    api.get("/auth/account/overview").then((r) => {
      setData(r.data);
      setProfile({ name: r.data.client?.name || r.data.user?.name || "", phone: r.data.client?.phone || "", ig: r.data.client?.ig || "" });
    }).catch(() => setData(false));
  }, [user]);

  if (user === null) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>;
  if (!user) return <Navigate to="/login?next=/account" replace />;

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await api.put("/auth/account/profile", profile);
      toast.success("Profile saved.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="account-page" className="max-w-5xl mx-auto px-6 py-20">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
        <div>
          <p className="text-xs tracking-[0.4em] uppercase text-brand mb-3">My Account</p>
          <h1 className="font-display text-5xl text-white">{profile.name || user.email}</h1>
          <p className="text-sm text-zinc-500 mt-2">{user.email}</p>
        </div>
        <button data-testid="account-logout-btn" onClick={logout}
          className="flex items-center gap-2 text-xs text-zinc-400 hover:text-red-400 border border-white/15 px-4 py-2.5 transition-colors">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>

      {data === null ? <p className="text-zinc-500">Loading…</p> : data === false ? <p className="text-red-400">Failed to load.</p> : (
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="border border-white/10 bg-[#0A0A0A] p-6 h-fit">
            <h2 className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-5">Profile</h2>
            <form onSubmit={saveProfile} className="space-y-4">
              <div>
                <Label className="text-xs text-zinc-400">Name</Label>
                <Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Phone</Label>
                <Input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Instagram</Label>
                <Input data-testid="profile-ig" value={profile.ig} onChange={(e) => setProfile({ ...profile, ig: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <button data-testid="profile-save-btn" type="submit"
                className="w-full bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase py-3 transition-colors">
                Save Profile
              </button>
            </form>
            <div className="mt-6 pt-6 border-t border-white/10">
              <h3 className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-3 flex items-center gap-2">
                <CreditCard className="w-3.5 h-3.5" /> Saved Card
              </h3>
              {data.client?.saved_card?.last4 ? (
                <p data-testid="saved-card" className="text-sm text-zinc-300 capitalize">
                  {data.client.saved_card.brand} •••• {data.client.saved_card.last4}
                  <span className="block text-xs text-zinc-500 mt-1">Auto-filled at your next checkout</span>
                </p>
              ) : (
                <p className="text-xs text-zinc-500">Your card is saved securely after your first purchase for faster checkout.</p>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4 flex items-center gap-2">
                <Package className="w-3.5 h-3.5" /> Orders
              </h2>
              {data.orders.length === 0 ? (
                <p className="text-sm text-zinc-500 border border-white/10 p-6">No orders yet. <Link to="/marketing" className="text-brand hover:underline">Browse campaigns</Link>.</p>
              ) : (
                <div className="border border-white/10 divide-y divide-white/5">
                  {data.orders.map((o) => (
                    <div key={o.id} data-testid={`account-order-${o.id}`} className="bg-[#0A0A0A] p-4 flex flex-wrap justify-between gap-3">
                      <div>
                        <p className="text-sm text-white">{o.items?.map((i) => i.name).join(", ")}</p>
                        <p className="text-xs text-zinc-500 mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-white">{fmt(o.total)}</p>
                        <p className={`text-[10px] uppercase tracking-widest mt-1 ${STATUS_COLORS[o.status] || "text-zinc-400"}`}>{o.status.replace(/_/g, " ")}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h2 className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4 flex items-center gap-2">
                <CalendarClock className="w-3.5 h-3.5" /> Bookings
              </h2>
              {data.bookings.length === 0 ? (
                <p className="text-sm text-zinc-500 border border-white/10 p-6">No bookings yet.</p>
              ) : (
                <div className="border border-white/10 divide-y divide-white/5">
                  {data.bookings.map((b) => (
                    <div key={b.id} data-testid={`account-booking-${b.id}`} className="bg-[#0A0A0A] p-4 flex flex-wrap justify-between gap-3">
                      <div>
                        <p className="text-sm text-white capitalize">{b.type} — {b.listing_title || b.details?.job_type || "Request"}</p>
                        <p className="text-xs text-zinc-500 mt-1">{b.details?.date || "Date TBD"}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-white">{fmt(b.estimate)}</p>
                        <p className={`text-[10px] uppercase tracking-widest mt-1 ${STATUS_COLORS[b.status] || "text-zinc-400"}`}>{b.status.replace(/_/g, " ")}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
